// auto-wrap.js - Obsidian-like auto-wrapping for markdown formatting

import { EditorView } from "@codemirror/view";

// Obsidian-like auto-wrapping for markdown formatting
// Exported for reuse in other editors (e.g., MCP Prompts Panel)
export const obsidianAutoWrap = EditorView.inputHandler.of((view, from, to, text) => {
  const selection = view.state.selection.main;

  // Only act if there's a selection
  if (selection.empty) {
    return false;
  }

  const selectedText = view.state.sliceDoc(selection.from, selection.to);

  // Handle # for headings (H1 -> H2 -> H3 -> H4 -> H5 -> H6)
  if (text === "#") {
    // Check current heading level
    const headingMatch = selectedText.match(/^(#{1,5})\s+(.+)$/);

    if (headingMatch) {
      // Increase heading level (add one more #)
      const currentHashes = headingMatch[1];
      const innerText = headingMatch[2];
      const newHashes = currentHashes + "#";

      view.dispatch({
        changes: {
          from: selection.from,
          to: selection.to,
          insert: `${newHashes} ${innerText}`
        },
        selection: {
          anchor: selection.from,
          head: selection.from + newHashes.length + 1 + innerText.length
        }
      });
      return true;
    }

    // First # - make it H1
    view.dispatch({
      changes: {
        from: selection.from,
        to: selection.to,
        insert: `# ${selectedText}`
      },
      selection: {
        anchor: selection.from,
        head: selection.from + selectedText.length + 2
      }
    });
    return true;
  }

  // Handle * for italic/bold
  if (text === "*") {
    // Check if the selected text already has single * (for upgrading to **)
    if (selectedText.startsWith("*") && selectedText.endsWith("*") && !selectedText.startsWith("**")) {
      // Remove outer * and add **
      const innerText = selectedText.slice(1, -1);
      view.dispatch({
        changes: {
          from: selection.from,
          to: selection.to,
          insert: `**${innerText}**`
        },
        selection: {
          anchor: selection.from + 2,
          head: selection.from + 2 + innerText.length + 2
        }
      });
      return true;
    }

    // First * - wrap with * and keep selection
    view.dispatch({
      changes: {
        from: selection.from,
        to: selection.to,
        insert: `*${selectedText}*`
      },
      selection: {
        anchor: selection.from,
        head: selection.from + selectedText.length + 2
      }
    });
    return true;
  }

  // Handle ` for inline code
  if (text === "`") {
    view.dispatch({
      changes: {
        from: selection.from,
        to: selection.to,
        insert: "`" + selectedText + "`"
      },
      selection: {
        anchor: selection.from,
        head: selection.from + selectedText.length + 2
      }
    });
    return true;
  }

  // Handle ~ for strikethrough
  if (text === "~") {
    view.dispatch({
      changes: {
        from: selection.from,
        to: selection.to,
        insert: "~~" + selectedText + "~~"
      },
      selection: {
        anchor: selection.from,
        head: selection.from + selectedText.length + 4
      }
    });
    return true;
  }

  // Handle _ for italic (alternative)
  if (text === "_") {
    view.dispatch({
      changes: {
        from: selection.from,
        to: selection.to,
        insert: "_" + selectedText + "_"
      },
      selection: {
        anchor: selection.from,
        head: selection.from + selectedText.length + 2
      }
    });
    return true;
  }

  // Handle = for highlight/mark (=text= -> ==text==)
  if (text === "=") {
    // Check if already wrapped with single = (upgrade to ==)
    if (selectedText.startsWith("=") && selectedText.endsWith("=") && !selectedText.startsWith("==")) {
      const innerText = selectedText.slice(1, -1);
      view.dispatch({
        changes: {
          from: selection.from,
          to: selection.to,
          insert: `==${innerText}==`
        },
        selection: {
          anchor: selection.from,
          head: selection.from + innerText.length + 4
        }
      });
      return true;
    }

    // First = - wrap with single =
    view.dispatch({
      changes: {
        from: selection.from,
        to: selection.to,
        insert: "=" + selectedText + "="
      },
      selection: {
        anchor: selection.from,
        head: selection.from + selectedText.length + 2
      }
    });
    return true;
  }

  // Handle [ for bracket/purple highlight
  if (text === "[") {
    view.dispatch({
      changes: {
        from: selection.from,
        to: selection.to,
        insert: "[" + selectedText + "]"
      },
      selection: {
        anchor: selection.from,
        head: selection.from + selectedText.length + 2
      }
    });
    return true;
  }

  return false;
});
